import React, { useState } from "react";
import { FaChevronRight, FaChevronDown, FaFolder, FaFile } from "react-icons/fa";

const CategoryTreeSelector = ({ 
  categories = [], 
  selectedId = "", 
  onSelect, 
  placeholder = "Select category",
  label = "Category",
  required = false,
  disabled = false,
  className = "" 
}) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [isOpen, setIsOpen] = useState(false);

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleSelect = (category) => {
    // Only allow selection of leaf nodes
    if (category.isLeaf) {
      onSelect(category);
      setIsOpen(false);
    }
  };

  const getSelectedCategoryName = () => {
    const findCategory = (cats, targetId) => {
      for (const cat of cats) {
        if (cat.id == targetId) return cat;
        if (cat.children && cat.children.length > 0) {
          const found = findCategory(cat.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    
    const selected = findCategory(categories, selectedId);
    return selected ? selected.full_path || selected.name : "";
  };

  const renderTreeNode = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedNodes.has(category.id);
    const isSelected = category.id == selectedId;

    return (
      <div key={category.id} className="select-none">
        {/* Node Row */}
        <div
          className={`flex items-center px-2 py-2 cursor-pointer hover:bg-slate-50 ${
            isSelected ? "bg-blue-50 border-l-2 border-l-blue-500" : ""
          } ${!category.isLeaf ? "text-slate-600" : "text-slate-800"}`}
          onClick={() => {
            if (hasChildren) {
              toggleNode(category.id);
            } else {
              handleSelect(category);
            }
          }}
        >
          {/* Indentation Spacer */}
          <div style={{ width: `${level * 24}px` }} className="flex-shrink-0" />
          
          {/* Expand/Collapse Icon */}
          <div className="w-4 h-4 flex items-center justify-center mr-2 flex-shrink-0">
            {hasChildren ? (
              isExpanded ? (
                <FaChevronDown className="w-3 h-3 text-slate-400" />
              ) : (
                <FaChevronRight className="w-3 h-3 text-slate-400" />
              )
            ) : (
              <div className="w-3 h-3" />
            )}
          </div>

          {/* Category Icon */}
          <div className="mr-2 flex-shrink-0">
            {category.isLeaf ? (
              <FaFile className="w-3 h-3 text-blue-500" />
            ) : (
              <FaFolder className="w-3 h-3 text-amber-500" />
            )}
          </div>

          {/* Category Name */}
          <span className={`text-sm font-medium flex-1 px-2 ${
            !category.isLeaf ? "italic" : ""
          } ${!category.isLeaf ? "cursor-default" : "cursor-pointer"}`}>
            {category.name}
            {!category.isLeaf && (
              <span className="text-xs text-slate-400 ml-1">(Category)</span>
            )}
          </span>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {category.children.map((child) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-semibold text-slate-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        {/* Selected Value Display */}
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm bg-white font-medium cursor-pointer flex items-center justify-between ${
            disabled 
              ? "bg-slate-50 text-slate-400 cursor-not-allowed" 
              : "hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          }`}
        >
          <span className={getSelectedCategoryName() ? "text-slate-800" : "text-slate-400"}>
            {getSelectedCategoryName() || placeholder}
          </span>
          <FaChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`} />
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
            <div className="py-2">
              {categories.length > 0 ? (
                categories.map((category) => renderTreeNode(category))
              ) : (
                <div className="px-4 py-3 text-slate-400 text-sm text-center">
                  No categories available
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-slate-500">
        Click folders to expand and view subcategories. Only specific items (files) can be selected.
      </p>

      {/* Click Outside Handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CategoryTreeSelector;
